
import { catchError, mergeMap, filter, mergeMapTo } from 'rxjs/operators'
import * as flatten from 'lodash/flatten'
import { change, focus, getFormAsyncErrors, getFormValues } from 'redux-form'
import { combineEpics } from 'redux-observable'
import { isExpectedError } from 'helpers/ajaxHelpers'
import {
  carDeletedActionCreator,
  carUnlinkedActionCreator,
  DELETE_CAR,
  DELETE_HOME,
  failDeleteCarActionCreator,
  failDeleteHomeActionCreator,
  failUnlinkCarActionCreator,
  failUnlinkHomeActionCreator,
  GET_CAR_MAKES,
  GET_CAR_MODELS,
  GET_CARS,
  GET_HOMES,
  homeDeletedActionCreator,
  homeUnlinkedActionCreator,
  setCarMakesActionCreator,
  setCarModelsActionCreator,
  setCarsActionCreator,
  setHomePriceActionCreator,
  setHomesActionCreator,
  UNLINK_CAR,
  UNLINK_HOME,
} from 'actions/propertyActions'
import { buildFieldArrayValuePath, getFormArrayValueFromAction, isHousingTypeWithZip } from 'helpers/formHelpers'
import { FORM_CARS_EDIT, FORM_HOMES_EDIT, FORM_ONBOARDING_PROPERTY } from 'constants/forms'
import { FORM_FIELD_CHANGE, FORM_FIELD_FOCUS, FormFieldFocus, FORM_SUBMITTED_SUCCESSFULLY } from 'actions/formActions'
import { merge, of, empty, from } from 'rxjs'
import { State } from 'interfaces/state'
import {
  DELETE_CAR_URL,
  DELETE_HOME_URL,
  GET_CAR_MAKES_URL,
  GET_CAR_MODELS_URL,
  GET_CARS_URL,
  GET_HOME_PRICE_URL,
  GET_HOMES_URL,
  UNLINK_CAR_URL,
  UNLINK_HOME_URL,
} from 'interfaces/ajax'
import { Epic } from 'interfaces/middleware'
import { captureEndpointError, captureWarning, PROPERTY_ASSESSMENT_FAILED } from 'helpers/errorHelpers'
import { normalize } from 'helpers/normalizeHelpers'
import { canSendHomePriceRequest, getHomePriceKey } from 'helpers/propertyHelpers'
import { HomeFormValues } from 'interfaces/property'
import { get, post } from 'helpers/observableAjaxHelpers'
import { INSIGHT_CTA_CLICK } from 'actions/insightsActions'
import { modifyFloatingPaneActionCreator, killFloatingPanesActionCreator } from 'actions/floatingPanesActions'
import { toggleHomeEditActionCreator, toggleCarEditActionCreator } from 'actions/settingsActions'
import { NEW_PROPERTY_ID } from 'constants/member'
import { addNotificationActionCreator } from 'actions/notificationsActions'
import { getInsightNotification } from 'helpers/notificationsHelpers'
import { Insight } from 'interfaces/insights'
import { byType } from 'rxOperators/byType'
import { mapSafe } from 'rxOperators/mergeMapSafe'
import { catchSafe } from 'rxOperators/catchSafe'

const changeHousingTypeEpic: Epic = (action$) =>
  action$
    .pipe(byType(FORM_FIELD_CHANGE)).pipe(
      mergeMap((a) => {
        const { form, field } = a.meta
        if (form === FORM_ONBOARDING_PROPERTY && field === 'housing_type' && isHousingTypeWithZip(a.payload)) {
          return of(focus(FORM_ONBOARDING_PROPERTY, 'zip5'))
        }

        return empty()
      }))


// ===========================================================================//
//                                     HOMES                                  //
// ===========================================================================//

const getHomesEpic: Epic = (action$) =>
  action$
    .pipe(byType(GET_HOMES)).pipe(
      mergeMap(() =>
        get(GET_HOMES_URL)
          .pipe(mapSafe(({ response }) =>
            setHomesActionCreator(
              normalize(response.results, 'id'),
            ),
          )),
      ))

const unlinkHomeEpic: Epic = (action$) =>
  action$
    .pipe(byType(UNLINK_HOME)).pipe(
      mergeMap((action) => {
        const { id } = action.payload
        const requestParams = {
          url: UNLINK_HOME_URL,
          params: {
            id: String(id),
          },
        }

        return post(requestParams).pipe(
          mergeMapTo(
            of(
              homeUnlinkedActionCreator(id),
            ),
          ))
          .pipe(catchSafe(() =>
            of(
              failUnlinkHomeActionCreator(id),
            ),
          ))
      }))

const deleteHomeEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(DELETE_HOME)).pipe(
      mergeMap((action) => {
        const homes = store.getState().data.property.homes
        if (!homes) {
          throw new Error(`Cannot handle ${ DELETE_HOME } without homes`)
        }

        const id = action.payload
        const home = homes.byKey[id]
        const requestParams = {
          url: DELETE_HOME_URL,
          params: {
            id: String(id),
          },
        }

        return post(requestParams).pipe(
          mergeMapTo(
            of(
              homeDeletedActionCreator(id),
            ),
          ))
          .pipe(catchSafe(() =>
            of(
              failDeleteHomeActionCreator(home),
            ),
          ))
      }))

type HomeFormReturn = {
  index?: number,
  field: string,
  values: HomeFormValues
  asyncErrors: any
}
// return home values form settings or property form
const getHomeForm = (action: FormFieldFocus, state: State): HomeFormReturn | null => {
  const { form, field } = action.meta

  if (form === FORM_ONBOARDING_PROPERTY) {
    return getFormArrayValueFromAction(action, FORM_ONBOARDING_PROPERTY, 'homes', state)
  } else if (form === FORM_HOMES_EDIT) {
    const values = getFormValues(FORM_HOMES_EDIT)(state) as HomeFormValues
    // TODO @pmyagkov use TS types for async errors
    const asyncErrors = getFormAsyncErrors(FORM_HOMES_EDIT)(state) as any || {}
    return {
      values,
      field,
      asyncErrors,
    }
  }

  return null
}

const getHomePriceEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(FORM_FIELD_FOCUS)).pipe(
      mergeMap((action) => {
        const state = store.getState()
        const homeForm = getHomeForm(action, state)

        if (!homeForm) {
          return empty()
        }

        const { field, values: formValues, asyncErrors } = homeForm

        if (!formValues || !field.includes('estimated_value')) {
          return empty()
        }

        const key = getHomePriceKey(formValues)

        if (!key || !canSendHomePriceRequest(asyncErrors)) {
          return empty()
        }

        return post(GET_HOME_PRICE_URL, { body: formValues }).pipe(
          mergeMap(({ response }) => {
            if (response.status === 'success') {
              return of(setHomePriceActionCreator(key, response))
            } else {
              captureWarning(PROPERTY_ASSESSMENT_FAILED, {
                property: 'home',
                error: response.detail,
              })
            }
            return empty()
          }),
          catchError((error) => {
          // We use the raw `catch` here intentionally.
          // The assessment feature is optional, we don't want the error screen to be shown to a user
          // if something goes wrong.
            if (!isExpectedError(error)) {
              captureEndpointError(error)
            }

            return empty()
          }))
      }))

// ===========================================================================//
//                                     CARS                                   //
// ===========================================================================//

const getCarsEpic: Epic = (action$) =>
  action$
    .pipe(byType(GET_CARS)).pipe(
      mergeMap(() =>
        get(GET_CARS_URL)
          .pipe(mapSafe(({ response }) =>
            setCarsActionCreator(
              normalize(response.results, 'id'),
            ),
          )),
      ))

const unlinkCarEpic: Epic = (action$) =>
  action$
    .pipe(byType(UNLINK_CAR)).pipe(
      mergeMap((action) => {
        const { id } = action.payload
        const requestParams = {
          url: UNLINK_CAR_URL,
          params: {
            id: String(id),
          },
        }

        return post(requestParams).pipe(
          mergeMapTo(
            of(
              carUnlinkedActionCreator(id),
            ),
          ))
          .pipe(catchSafe(() =>
            of(
              failUnlinkCarActionCreator(id),
            ),
          ))
      }))

const deleteCarEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(DELETE_CAR)).pipe(
      mergeMap((action) => {
        const { cars } = store.getState().data.property
        if (!cars) {
          throw new Error(`Cannot handle ${ DELETE_CAR } without cars`)
        }

        const id = action.payload
        const car = cars.byKey[id]
        const requestParams = {
          url: DELETE_CAR_URL,
          params: {
            id: String(id),
          },
        }

        return post(requestParams).pipe(
          mergeMapTo(
            of(
              carDeletedActionCreator(id),
            ),
          ))
          .pipe(catchSafe(() =>
            of(
              failDeleteCarActionCreator(car),
            ),
          ))
      }))

const getCarMakesEpic: Epic = (action$) =>
  action$
    .pipe(byType(GET_CAR_MAKES)).pipe(
      mergeMap(() =>
        get(GET_CAR_MAKES_URL)
          .pipe(mapSafe(({ response }) => setCarMakesActionCreator(response))),
      ))

const getCarModelsEpic: Epic = (action$) =>
  action$
    .pipe(byType(GET_CAR_MODELS)).pipe(
      mergeMap(({ payload }) =>
        get({ url: GET_CAR_MODELS_URL, params: { make: payload }})
          .pipe(mapSafe(({ response }) =>
            setCarModelsActionCreator(payload, response),
          )),
      ))

const getDependentCarFields = (field: string) => {
  const sequence = ['make', 'model', 'year', 'style']
  const index = sequence.indexOf(field)
  return index === -1
    ? []
    : sequence.slice(index + 1)
}

const getResetCarFieldsActions = (form: string, index: number | null, field: string, values: Record<string, string | null>) =>
  getDependentCarFields(field)
    // reset only filled fields
    .filter((dependentField) => values[dependentField] !== null)
    .map((dependentField) => {
      const fieldPath = form === FORM_ONBOARDING_PROPERTY && index !== null ? buildFieldArrayValuePath('cars', index, dependentField) : dependentField
      return change(form, fieldPath, null)
    })

const getCarInfoEpic: Epic = ((action$, store) =>
  action$
    .pipe(byType(FORM_FIELD_CHANGE)).pipe(
      mergeMap((action) => {
        if (!action.payload) {
          return empty()
        }

        const state = store.getState()
        const onboardingForm = action.meta.form === FORM_ONBOARDING_PROPERTY

        const data = onboardingForm
          ? getFormArrayValueFromAction(action, FORM_ONBOARDING_PROPERTY, 'cars', state)
          : {
            values: getFormValues(FORM_CARS_EDIT)(state),
            field: action.meta.field,
            index: null,
            asyncErrors: {},
          }

        if (!data) {
          return empty()
        }

        let request$ = null
        switch (data.field) {
          case 'make': {
            const make = action.payload
            if (!(make in state.data.property.carModels)) {
              request$ = get({ url: GET_CAR_MODELS_URL, params: { make }})
                .pipe(mapSafe(({ response }) =>
                  setCarModelsActionCreator(action.payload, response),
                ))
            }
            break
          }
        }

        const carValues = onboardingForm && data.index ?
          (getFormValues(FORM_ONBOARDING_PROPERTY)(state) as any).cars[data.index] :
          data.values
        const resetActions = getResetCarFieldsActions(action.meta.form, data.index, data.field, carValues)
        const resetActions$ = from(flatten(resetActions))

        if (request$) {
          return merge(request$, resetActions$)
        }
        return resetActions$
      }))
)


const handleAddHouseCTAEpic: Epic = (action$) =>
  action$
    .pipe(byType(INSIGHT_CTA_CLICK)).pipe(
      filter(({ payload }) => !!(payload.type === 'add_home')),
      mergeMap(() => from([
        toggleHomeEditActionCreator(NEW_PROPERTY_ID),
        modifyFloatingPaneActionCreator('house-edit', {}),
      ])))

const handleAddCarCTAEpic: Epic = (action$) =>
  action$
    .pipe(byType(INSIGHT_CTA_CLICK)).pipe(
      filter(({ payload }) => !!(payload.type === 'add_car')),
      mergeMap(() => from([
        toggleHomeEditActionCreator(NEW_PROPERTY_ID),
        modifyFloatingPaneActionCreator('car-edit', {}),
      ])))

const successSubmitHouseEpic: Epic = (action$) =>
  action$
    .pipe(byType(FORM_SUBMITTED_SUCCESSFULLY)).pipe(
      filter((a) => a.payload.formName === FORM_HOMES_EDIT),
      mergeMap(() => from([
        addNotificationActionCreator(getInsightNotification({
          id: -1 * Math.round(Math.random() * 999999),
          text: 'Your home as been added to your assets. You can edit it or add more homes from the Settings screen.',
          cta: {
            link: {
              target: 'settings_homes',
              theme: 'text-only',
              title: 'See Settings',
              filters: {
                start_date: `${ new Date() }`,
                end_date: `${ new Date() }`,
              },
            },
          },
        } as Insight)),
        toggleHomeEditActionCreator(null),
        killFloatingPanesActionCreator('house-edit'),
      ])))

const successSubmitCarEpic: Epic = (action$) =>
  action$
    .pipe(byType(FORM_SUBMITTED_SUCCESSFULLY)).pipe(
      filter((a) => a.payload.formName === FORM_CARS_EDIT),
      mergeMap(() => from([
        addNotificationActionCreator(getInsightNotification({
          id: -1 * Math.round(Math.random() * 999999),
          text: 'Your car as been added to your assets. You can edit it or add more homes from the Settings screen.',
          cta: {
            link: {
              target: 'settings_cars',
              theme: 'text-only',
              title: 'See Settings',
              filters: {
                start_date: `${ new Date() }`,
                end_date: `${ new Date() }`,
              },
            },
          },
        } as Insight)),
        toggleCarEditActionCreator(null),
        killFloatingPanesActionCreator('car-edit'),
      ])))

export default combineEpics(
  changeHousingTypeEpic,
  getHomesEpic,
  unlinkHomeEpic,
  deleteHomeEpic,
  getHomePriceEpic,
  getCarsEpic,
  unlinkCarEpic,
  deleteCarEpic,
  getCarMakesEpic,
  getCarInfoEpic,
  getCarModelsEpic,
  successSubmitHouseEpic,
  successSubmitCarEpic,
  handleAddHouseCTAEpic,
  handleAddCarCTAEpic,
)
